/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Mail, MessageCircle, Clock, Calculator } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const COUNTRIES = [
  'Thailand',
  'India',
  'Indonesia',
  'Nigeria',
  'South Africa',
  'Brazil',
  'Mexico',
  'Egypt',
  'Kenya',
  'Philippines',
  'United States',
  'Malaysia',
  'Vietnam',
  'Pakistan',
  'Bangladesh',
  'Turkey',
  'Saudi Arabia',
  'UAE',
  'Colombia',
  'Ghana',
];

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut' as const },
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    whatsapp: '',
    country: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Wire up to backend / email service
    console.log('Contact form submitted:', formData);
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Banner */}
      <section className="bg-slate-900 py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <motion.h1
            className="text-4xl md:text-5xl font-bold text-white mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Get in Touch
          </motion.h1>
          <motion.p
            className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            Have questions about thermal conversion equipment? We respond within 24 hours.
          </motion.p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Column — Contact Information */}
            <motion.div {...fadeUp}>
              <h2 className="text-2xl font-bold text-slate-900 mb-8">
                Contact Information
              </h2>

              <div className="space-y-6">
                {/* Email Card */}
                <div className="flex items-start gap-4 p-5 bg-white rounded-2xl border shadow-sm">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Email</p>
                    <p className="text-slate-600 mt-1">contact@verigreenco.com</p>
                  </div>
                </div>

                {/* WhatsApp Card */}
                <div className="flex items-start gap-4 p-5 bg-white rounded-2xl border shadow-sm">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#25D366]/10 flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-[#25D366]" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">WhatsApp</p>
                    <p className="text-slate-600 mt-1 text-sm mb-3">
                      Chat with us instantly
                    </p>
                    <a
                      href="https://wa.me/85259379574?text=Hi%20VERIGREEN%2C%20I%27m%20interested%20in%20pyrolysis%20equipment"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white font-medium text-sm shadow-md hover:shadow-lg transition-all"
                      style={{ backgroundColor: '#25D366' }}
                    >
                      <MessageCircle className="w-4 h-4" />
                      Chat on WhatsApp
                    </a>
                  </div>
                </div>

                {/* Response Time Card */}
                <div className="flex items-start gap-4 p-5 bg-white rounded-2xl border shadow-sm">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Response Time</p>
                    <p className="text-slate-600 mt-1">
                      We typically respond within 24 hours
                    </p>
                  </div>
                </div>

                {/* ROI Calculator Link */}
                <Link
                  to="/roi-calculator"
                  className="flex items-center gap-3 p-5 bg-primary/5 rounded-2xl border border-primary/20 hover:bg-primary/10 transition-colors group"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Calculator className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">
                      Prefer to start with numbers?
                    </p>
                    <p className="text-primary text-sm mt-0.5 group-hover:underline">
                      Try our ROI Calculator →
                    </p>
                  </div>
                </Link>
              </div>
            </motion.div>

            {/* Right Column — Contact Form */}
            <motion.div {...fadeUp} transition={{ duration: 0.6, ease: 'easeOut', delay: 0.1 }}>
              {submitted ? (
                <div className="bg-white rounded-2xl border shadow-lg p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">
                    Message Sent!
                  </h3>
                  <p className="text-slate-600">
                    Thank you for reaching out. We'll get back to you within 24 hours.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="bg-white rounded-2xl border shadow-lg p-8 space-y-5"
                >
                  {/* Name */}
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-slate-700 mb-1.5"
                    >
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      className="w-full px-4 py-3 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-slate-700 mb-1.5"
                    >
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@company.com"
                      className="w-full px-4 py-3 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                    />
                  </div>

                  {/* WhatsApp Number */}
                  <div>
                    <label
                      htmlFor="whatsapp"
                      className="block text-sm font-medium text-slate-700 mb-1.5"
                    >
                      WhatsApp Number
                    </label>
                    <input
                      id="whatsapp"
                      name="whatsapp"
                      type="tel"
                      value={formData.whatsapp}
                      onChange={handleChange}
                      placeholder="+1 234 567 8900"
                      className="w-full px-4 py-3 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                    />
                  </div>

                  {/* Country */}
                  <div>
                    <label
                      htmlFor="country"
                      className="block text-sm font-medium text-slate-700 mb-1.5"
                    >
                      Country
                    </label>
                    <select
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                    >
                      <option value="">Select your country</option>
                      {COUNTRIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-slate-700 mb-1.5"
                    >
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={4}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project or question..."
                      className="w-full px-4 py-3 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition resize-vertical"
                    />
                  </div>

                  {/* Submit */}
                  <Button type="submit" variant="brand" size="lg" className="w-full">
                    Send Message
                  </Button>

                  {/* WhatsApp fallback */}
                  <p className="text-center text-sm text-slate-500">
                    Or{' '}
                    <a
                      href="https://wa.me/85259379574?text=Hi%20VERIGREEN%2C%20I%27m%20interested%20in%20pyrolysis%20equipment"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#25D366] font-medium hover:underline"
                    >
                      chat with us on WhatsApp →
                    </a>
                  </p>
                </form>
              )}
            </motion.div>
          </div>

          {/* Privacy Note */}
          <motion.p
            className="text-center text-sm text-slate-400 mt-12"
            {...fadeUp}
          >
            Your information is kept confidential. We never share your data with third parties.
          </motion.p>
        </div>
      </section>
    </div>
  );
};

export default Contact;
